import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import './TimeIntervalPicker.scss';

interface TimeIntervalPickerProps {
	calendarDate?: Date;
	startTime?: Date | null;
	endTime?: Date | null;
	onChange?: (startTime: Date | null, endTime: Date | null) => void;
	maxDuration?: number;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	error?: boolean;
}

const TimeIntervalPicker: React.FC<TimeIntervalPickerProps> = ({
	calendarDate,
	startTime,
	endTime,
	onChange,
	maxDuration = 1440,
	placeholder = 'Выбрать',
	disabled = false,
	className = '',
	error = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [start, setStart] = useState({ hours: 0, minutes: 0 });
	const [end, setEnd] = useState({ hours: 0, minutes: 0 });
	const [duration, setDuration] = useState(0);
	const pickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (startTime) {
			setStart({
				hours: startTime.getHours(),
				minutes: startTime.getMinutes(),
			});
		}

		if (endTime) {
			setEnd({
				hours: endTime.getHours(),
				minutes: endTime.getMinutes(),
			});
		}
	}, [startTime, endTime]);

	useEffect(() => {
		const startMinutes = start.hours * 60 + start.minutes;
		const endMinutes = end.hours * 60 + end.minutes;
		const MINUTES_PER_DAY = 1440;
		const newDuration =
			endMinutes > startMinutes ? endMinutes - startMinutes : MINUTES_PER_DAY - startMinutes + endMinutes;

		setDuration(newDuration);
	}, [start, end]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const getTimeAsDate = (hours: number, minutes: number): Date => {
		const date = moment(calendarDate).clone().toDate() ?? new Date();
		date.setHours(hours, minutes, 0, 0);
		return date;
	};

	const handleStartTimeChange = (type: 'hours' | 'minutes', value: string) => {
		const numValue = parseInt(value) || 0;
		let validatedValue = numValue;

		if (type === 'hours') {
			validatedValue = Math.max(0, Math.min(23, numValue));
		} else {
			validatedValue = Math.max(0, Math.min(59, numValue));
		}

		const newStart = { ...start, [type]: validatedValue };
		setStart(newStart);

		const startMinutes = newStart.hours * 60 + newStart.minutes;
		const endMinutes = end.hours * 60 + end.minutes;

		let newEndMinutes = endMinutes;
		if (endMinutes < startMinutes) {
			newEndMinutes = startMinutes + Math.min(duration, maxDuration);
		}

		if (newEndMinutes > 1439) {
			newEndMinutes = 1439;
		}

		const newEnd = {
			hours: Math.floor(newEndMinutes / 60),
			minutes: newEndMinutes % 60,
		};

		setEnd(newEnd);
	};

	const handleEndTimeChange = (type: 'hours' | 'minutes', value: string) => {
		const numValue = parseInt(value) || 0;
		let validatedValue = numValue;

		if (type === 'hours') {
			validatedValue = Math.max(0, Math.min(23, numValue));
		} else {
			validatedValue = Math.max(0, Math.min(59, numValue));
		}

		const newEnd = { ...end, [type]: validatedValue };
		setEnd(newEnd);
	};

	const handleApply = () => {
		const startDate = getTimeAsDate(start.hours, start.minutes);
		const endDate = getTimeAsDate(end.hours, end.minutes);
		onChange?.(
			startDate,
			startDate.getTime() > endDate.getTime() ? moment(endDate).add(1, 'day').toDate() : endDate,
		);
		setIsOpen(false);
	};

	const handleClear = () => {
		setStart({ hours: 0, minutes: 0 });
		setEnd({ hours: 0, minutes: 0 });
		onChange?.(null, null);
		setIsOpen(false);
	};

	const formatDisplayInterval = (): string => {
		if (!startTime || !endTime) return '';

		const formatTime = (date: Date) =>
			date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			});

		return `${formatTime(startTime)} - ${formatTime(endTime)}`;
	};

	const formatDuration = (minutes: number): string => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;

		if (hours === 0) return `${mins} мин`;
		if (mins === 0) return `${hours} ч`;
		return `${hours} ч ${mins} мин`;
	};

	const formatTimeValue = (value: number): string => {
		return value.toString().padStart(2, '0');
	};

	const containerClasses = [
		'time-interval-picker',
		className,
		isOpen && 'is-open',
		error && 'has-error',
		disabled && 'is-disabled',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={containerClasses} ref={pickerRef}>
			<div className="time-interval-picker__input" onClick={() => !disabled && setIsOpen(!isOpen)}>
				<input
					type="text"
					readOnly
					value={formatDisplayInterval()}
					placeholder={placeholder}
					disabled={disabled}
					className="time-interval-picker__display"
				/>
			</div>

			{isOpen && (
				<div className="time-interval-picker__dropdown">
					<div className="time-interval-picker__header">
						<h3>Выбор интервала времени</h3>
						<div className="time-interval-picker__duration-info">
							Длительность: <strong>{formatDuration(duration)}</strong>
							{duration > maxDuration && (
								<span className="time-interval-picker__error-text">
									(максимум {formatDuration(maxDuration)})
								</span>
							)}
						</div>
					</div>

					<div className="time-interval-picker__time-section">
						<label className="time-interval-picker__time-label">Начало</label>
						<div className="time-interval-picker__time-controls">
							<div className="time-interval-picker__input-group">
								<label>Часы</label>
								<input
									type="number"
									min="0"
									max="23"
									value={formatTimeValue(start.hours)}
									onChange={e => handleStartTimeChange('hours', e.target.value)}
									className="time-interval-picker__time-input"
									placeholder="00"
								/>
							</div>
							<div className="time-interval-picker__separator">:</div>
							<div className="time-interval-picker__input-group">
								<label>Минуты</label>
								<input
									type="number"
									min="0"
									max="59"
									value={formatTimeValue(start.minutes)}
									onChange={e => handleStartTimeChange('minutes', e.target.value)}
									className="time-interval-picker__time-input"
									placeholder="00"
								/>
							</div>
						</div>
					</div>

					<div className="time-interval-picker__time-section">
						<label className="time-interval-picker__time-label">Окончание</label>
						<div className="time-interval-picker__time-controls">
							<div className="time-interval-picker__input-group">
								<label>Часы</label>
								<input
									type="number"
									min="0"
									max="23"
									value={formatTimeValue(end.hours)}
									onChange={e => handleEndTimeChange('hours', e.target.value)}
									className="time-interval-picker__time-input"
									placeholder="00"
								/>
							</div>
							<div className="time-interval-picker__separator">:</div>
							<div className="time-interval-picker__input-group">
								<label>Минуты</label>
								<input
									type="number"
									min="0"
									max="59"
									value={formatTimeValue(end.minutes)}
									onChange={e => handleEndTimeChange('minutes', e.target.value)}
									className="time-interval-picker__time-input"
									placeholder="00"
								/>
							</div>
						</div>
					</div>

					<div className="time-interval-picker__actions">
						<button
							type="button"
							className="time-interval-picker__action-btn time-interval-picker__action-btn--clear"
							onClick={handleClear}
						>
							Очистить
						</button>
						<button type="button" className="button button--solid" onClick={handleApply}>
							Применить
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default TimeIntervalPicker;
